import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, MenuItem, Select, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert, TablePagination } from "@mui/material";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { format, parseISO, isValid } from 'date-fns';
import { getData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

function HolidayCalendar() {
    const [year, setYear] = useState('2026');
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const orgId = userData?.organization_id;
    const locId = userData?.location_id;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchHolidays = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = API_ENDPOINTS.getHolidaysByYearAndMonth(year, orgId, locId);
            const response = await getData(url);
            if (response?.status === "SUCCESS" && response?.message?.holidays) {
                setHolidays(response.message.holidays);
            } else {
                setHolidays([]);
            }
        } catch (err) {
            setError("Failed to load holiday calendar.");
            setHolidays([]);
        } finally {
            setLoading(false);
        }
    }, [year, orgId, locId]);

    useEffect(() => {
        fetchHolidays();
        setPage(0);
    }, [fetchHolidays]);

    const formatDate = (dateString, formatStr) => {
        if (!dateString) return "N/A";
        const date = parseISO(dateString);
        return isValid(date) ? format(date, formatStr) : "Invalid Date";
    };

    const handleDownloadPDF = async () => {
        try {
            const path = API_ENDPOINTS.downloadHolidayListPDF(year, orgId, locId);
            const response = await getData(path, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Holiday_List_${year}.pdf`);
            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert("Could not download the holiday list. Please try again.");
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedHolidays = (holidays || []).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ backgroundColor: 'white', p: 1.5, px: 2, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>YEAR</Typography>
                    <Select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        variant="standard"
                        sx={{ fontWeight: 700, minWidth: 80, '&:before, &:after': { display: 'none' } }}
                    >
                        <MenuItem value="2025">2025</MenuItem>
                        <MenuItem value="2026">2026</MenuItem>
                        <MenuItem value="2027">2027</MenuItem>
                    </Select>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleDownloadPDF}
                    sx={{
                        textTransform: 'none', color: 'text.primary', borderColor: '#e0e0e0',
                        borderRadius: 2, fontWeight: 600, px: 3, py: 1, backgroundColor: 'white',
                        '&:hover': { borderColor: '#bdbdbd', backgroundColor: '#f5f5f5' }
                    }}
                >
                    Export PDF
                </Button>
            </Box>

            <TableContainer sx={{ backgroundColor: 'white', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
                ) : (
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ backgroundColor: '#fafafa' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem', py: 2 }}>DATE</TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem' }}>DAY</TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem' }}>HOLIDAY NAME</TableCell>
                                <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem' }}>TYPE</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedHolidays.length > 0 ? (
                                paginatedHolidays.map((holiday, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell sx={{ fontWeight: 700, py: 3 }}>
                                            {formatDate(holiday.date, 'dd MMM yyyy')}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                            {formatDate(holiday.date, 'EEEE')}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            {holiday.holiday_name}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                label={holiday.is_national ? 'NATIONAL' : 'GENERAL'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#eef2ff', color: '#6366f1',
                                                    fontWeight: 800, fontSize: '0.65rem', borderRadius: 25, px: 2
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 10, color: 'text.secondary' }}>
                                        No holidays found for this year.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <TablePagination
                component="div"
                count={holidays.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[1, 5, 10, 25]}
            />
        </Box>
    );
}

export default HolidayCalendar;